import { useState } from 'react';
import { getRenamedFile, uploadFileToBucket } from './helpers';

type InitializeUploadDataType = {
  data: { data: { filename: string } };
};

type PrepareFileUploadResponseType = {
  uuid: string;
  preSignedUrl: string;
};

type InitializeUploadDataResponseType = {
  data: PrepareFileUploadResponseType;
};

type UseFileUploadReturnType = {
  uploadFile: (file: File) => Promise<UploadFileReturnType | void>;
  uploadedFile: UploadedFile | null;
  isUploading: boolean;
  clearUploadedFile: () => void;
};

type UseFileUploadPropsType = {
  initializeUpload: (
    data: InitializeUploadDataType
  ) => Promise<InitializeUploadDataResponseType>;
};

type UploadFileReturnType = {
  fileUuid: string;
};

export type UploadedFile = {
  file: File;
  uuid: string;
};

export const useFileUpload = ({
  initializeUpload,
}: UseFileUploadPropsType): UseFileUploadReturnType => {
  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const prepareFileUpload = async (
    file: File
  ): Promise<PrepareFileUploadResponseType> => {
    const { data } = await initializeUpload({
      data: { data: { filename: file.name } },
    });

    return data;
  };

  const uploadFile = async (
    file: File
  ): Promise<UploadFileReturnType | void> => {
    setIsUploading(true);
    setUploadedFile(null);
    try {
      const { uuid, preSignedUrl } = await prepareFileUpload(file);
      const renamedFile = getRenamedFile({ inputFile: file, newName: uuid });

      await uploadFileToBucket(renamedFile, preSignedUrl);

      setUploadedFile({ file, uuid });
      return { fileUuid: uuid };
    } finally {
      setIsUploading(false);
    }
  };

  const clearUploadedFile = () => {
    setUploadedFile(null);
  };

  return {
    uploadFile,
    uploadedFile,
    isUploading,
    clearUploadedFile,
  };
};
